import { Link } from '@inertiajs/react'
import InvestorPortalLayout from '@/layouts/InvestorPortalLayout'

interface Offering {
  id: number
  loan_identifier: string
  proposed_principal_amount: number
  proposed_ownership_percent: number
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'withdrawn'
  created_at: string
  expires_at: string | null
  lender_business_name: string
}

interface Props {
  offerings: Offering[]
}

const fmtMoney = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const statusStyles: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  declined: 'bg-red-50 text-red-700 border-red-200',
}

export default function OfferingsIndex({ offerings }: Props) {
  if (offerings.length === 0) {
    return (
      <InvestorPortalLayout>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Offerings</h1>
          <p className="text-sm text-gray-500 mt-1">Loan participation proposals from your lender.</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839 2.51l-4.66-2.51m0 0l-1.023-.55a2.25 2.25 0 00-2.134 0l-1.022.55m0 0l-4.661 2.51" />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-gray-900 mb-1">No loan offerings yet</h2>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            Your lender will invite you to participate in deals here. Check back after they send you an offering.
          </p>
        </div>
      </InvestorPortalLayout>
    )
  }

  return (
    <InvestorPortalLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Offerings</h1>
        <p className="text-sm text-gray-500 mt-1">Loan participation proposals from your lender.</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 font-medium text-gray-500">Loan</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500">Proposed Principal</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500">Ownership %</th>
                <th className="text-center px-4 py-3 font-medium text-gray-500">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Received</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {offerings.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-medium text-gray-900">{o.loan_identifier}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-900">{fmtMoney(o.proposed_principal_amount)}</td>
                  <td className="px-4 py-3 text-right font-mono text-gray-900">{o.proposed_ownership_percent}%</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full border ${statusStyles[o.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                      {o.status.charAt(0).toUpperCase() + o.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      href={`/investor-portal/offerings/${o.id}`}
                      className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </InvestorPortalLayout>
  )
}
